import type { ClipboardAction } from "./use-canvas-clipboard";

export function isMacPlatform(platform = navigator.platform) { return /Mac|iPhone|iPad|iPod/.test(platform); }
export function shortcutModifier(event: { metaKey: boolean; ctrlKey: boolean }, platform = navigator.platform) {
  return isMacPlatform(platform) ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}
export function shortcutHint(key: string, platform = navigator.platform) { return `${isMacPlatform(platform) ? "⌘" : "Ctrl+"}${key}`; }

const nativeControls = "input, textarea, select, [contenteditable]:not([contenteditable='false']), dialog, [role='dialog'], [role='alertdialog'], button, a[href], summary, [data-canvas-controls], [role='button'], [role='link'], [role='menu'], [role='menuitem'], [role='menuitemcheckbox'], [role='menuitemradio'], [role='combobox'], [role='listbox'], [role='option'], [role='slider'], [role='spinbutton'], [role='textbox'], [role='checkbox'], [role='radio'], [role='switch'], [role='tab']";
export function isCanvasShortcutTarget(target: EventTarget | null, canvas: HTMLElement | null): boolean {
  if (!(target instanceof Element) || !canvas?.contains(target)) return false;
  const control = target.closest(nativeControls);
  // React Flow's focusable node wrapper is the canvas object, not a native button.
  return !control || (control.matches(".react-flow__node[role='button']") &&
    !control.parentElement?.closest(nativeControls));
}

export const CANVAS_SHORTCUTS = {
  select: { label: "Select mode", key: "V" }, pan: { label: "Pan mode", key: "H" },
  temporaryPan: { label: "Temporary pan (hold)", key: "Space" },
  selectAll: { label: "Select all", key: "A", mod: true }, cancel: { label: "Cancel / deselect", key: "Escape" },
  zoomIn: { label: "Zoom in", key: "+" }, zoomOut: { label: "Zoom out", key: "-" },
  actualSize: { label: "Actual size", key: "0" }, fitBoard: { label: "Fit board", key: "1" },
  fitSelection: { label: "Fit selection", key: "2" }, shortcuts: { label: "Keyboard shortcuts", key: "?" },
  undo: { label: "Undo", key: "Z", mod: true }, redo: { label: "Redo", key: "Shift+Z", mod: true },
  copy: { label: "Copy", key: "C", mod: true }, cut: { label: "Cut", key: "X", mod: true },
  paste: { label: "Paste", key: "V", mod: true }, duplicate: { label: "Duplicate", key: "D", mod: true },
  delete: { label: "Delete selection", key: "Delete / Backspace" },
} as const;
export type CanvasShortcut = keyof typeof CANVAS_SHORTCUTS;
export function canvasShortcutHint(action: CanvasShortcut, platform = navigator.platform): string {
  const entry = CANVAS_SHORTCUTS[action];
  return "mod" in entry ? shortcutHint(entry.key, platform) : entry.key;
}
export function canvasAriaShortcut(action: CanvasShortcut, platform = navigator.platform): string {
  if (action === "delete") return "Delete Backspace";
  if (action === "zoomIn") return "= Shift+Plus";
  if (action === "shortcuts") return "Shift+?";
  return canvasShortcutHint(action, platform).replace("⌘", "Meta+").replace("Ctrl+", "Control+");
}
export type CanvasKey = {
  key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean;
  repeat: boolean; defaultPrevented: boolean; isComposing: boolean;
};
export function canvasShortcut(event: CanvasKey, platform = navigator.platform): CanvasShortcut | null {
  if (event.altKey || event.defaultPrevented || event.isComposing) return null;
  const key = event.key.toLowerCase(), modified = event.metaKey || event.ctrlKey;
  if (modified && !shortcutModifier(event, platform)) return null;
  if (!modified && event.shiftKey && !["+", "?", "/"].includes(key)) return null;
  const binding = modified ? `${event.shiftKey ? "shift+" : ""}${key}`
    : key === " " ? "space" : key === "=" ? "+" : key === "/" && event.shiftKey ? "?"
    : key === "delete" || key === "backspace" ? "delete / backspace" : key;
  const action = modified && !isMacPlatform(platform) && binding === "y" ? "redo"
    : (Object.keys(CANVAS_SHORTCUTS) as CanvasShortcut[]).find(action => {
      const entry = CANVAS_SHORTCUTS[action];
      return ("mod" in entry) === modified && entry.key.toLowerCase() === binding;
    });
  return action && (!event.repeat || ["zoomIn", "zoomOut", "temporaryPan"].includes(action)) ? action : null;
}
export function clipboardShortcut(event: CanvasKey, platform = navigator.platform): ClipboardAction | null {
  const action = canvasShortcut(event, platform);
  return action && ["copy", "cut", "paste", "duplicate"].includes(action) ? action as ClipboardAction : null;
}
